// 设置：账号（改密码）、AI 头像、语音输入（识别模型）、API Key（新建时显示一次完整的，之后只显示遮住的；吊销）、对外接入（MCP）、模型（只读）、关于。

import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp, Modal } from 'antd'
import { Copy, KeyRound, Plus } from 'lucide-react'
import { CAPABILITY_LABEL, changePassword, createApiKey, getMe, listApiKeys, listModels, maskKey, passwordProblem, revokeApiKey, systemInfo, type ApiKey } from '../../api/settings'
import { Pill } from '../../ui'
import { useKnowledgeBases } from '../chat/Composer'
import { AVATAR_GROUPS, AvatarMark, sameAvatar, setAvatar, useAvatar } from '../../ui/AiAvatar'
import { VoiceSettings } from '../voice/VoiceSettings'
import { OpenAccess } from './OpenAccess'
import '../kb/dialogs.css'
import '../kb/kb.css'
import './settings.css'

type Section = 'account' | 'avatar' | 'voice' | 'keys' | 'open' | 'models' | 'about'
const SECTIONS: { key: Section; label: string }[] = [
  { key: 'account', label: '账号' },
  { key: 'avatar', label: 'AI 头像' },
  { key: 'voice', label: '语音输入' },
  { key: 'keys', label: 'API Key' },
  { key: 'open', label: '对外接入' },
  { key: 'models', label: '模型' },
  { key: 'about', label: '关于' },
]
const when = (iso?: string) => (iso ? new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) : '—')

export function SettingsPage() {
  const [params, setParams] = useSearchParams()
  const section = (SECTIONS.some((s) => s.key === params.get('section')) ? params.get('section') : 'account') as Section
  return (
    <div className="page settings">
      <div className="page-h">
        <h1>设置</h1>
      </div>
      <div className="settings-grid">
        <nav className="settings-nav" aria-label="设置">
          {SECTIONS.map((s) => (
            <button key={s.key} type="button" aria-current={s.key === section ? 'page' : undefined} onClick={() => setParams({ section: s.key })}>
              {s.label}
            </button>
          ))}
        </nav>
        <div className="settings-body">
          {section === 'account' && <Account />}
          {section === 'avatar' && <Avatars />}
          {section === 'voice' && <VoiceSettings />}
          {section === 'keys' && <Keys />}
          {section === 'open' && <OpenAccess />}
          {section === 'models' && <Models />}
          {section === 'about' && <About />}
        </div>
      </div>
    </div>
  )
}

function Account() {
  const { message } = AntApp.useApp()
  const me = useQuery({ queryKey: ['me'], queryFn: getMe })
  const [old, setOld] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const problem = next ? passwordProblem(next) : null
  const mismatch = again && again !== next ? '两次输入的不一样' : null
  const change = useMutation({
    mutationFn: () => changePassword(old, next),
    onSuccess: () => {
      setOld('')
      setNext('')
      setAgain('')
      message.success('密码改好了，下次登录用新密码')
    },
    onError: (e) => void message.error(e.message),
  })
  const u = me.data?.user
  return (
    <>
      <section className="settings-card" aria-label="账号">
        <h2>账号</h2>
        {me.isError && <p className="field-note">没能载入：{me.error.message}</p>}
        <dl className="settings-dl">
          <dt>名字</dt>
          <dd>{u?.username ?? '…'}</dd>
          <dt>邮箱</dt>
          <dd>{u?.email ?? '…'}</dd>
          <dt>空间</dt>
          <dd>{me.data?.tenant?.name ?? '…'}</dd>
          {u?.is_system_admin && (
            <>
              <dt>身份</dt>
              <dd>系统管理员</dd>
            </>
          )}
        </dl>
      </section>
      <section className="settings-card" aria-label="改密码">
        <h2>改密码</h2>
        <form
          className="dlg"
          onSubmit={(e) => {
            e.preventDefault()
            if (old && next && !problem && !mismatch && again) change.mutate()
          }}
        >
          <label htmlFor="pw-old">现在的密码</label>
          <input id="pw-old" type="password" autoComplete="current-password" value={old} onChange={(e) => setOld(e.target.value)} />
          <label htmlFor="pw-new">新密码</label>
          <input id="pw-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} aria-describedby="pw-rule" />
          <p id="pw-rule" className="field-note" data-bad={!!problem}>
            {problem ? `新密码${problem}` : '8–32 位，要同时有字母和数字。'}
          </p>
          <label htmlFor="pw-again">再输一次新密码</label>
          <input id="pw-again" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
          {mismatch && (
            <p className="field-note" data-bad="true">
              {mismatch}
            </p>
          )}
          <div className="dlg-acts">
            <Pill type="submit" variant="primary" disabled={!old || !next || !again || !!problem || !!mismatch || change.isPending}>
              改密码
            </Pill>
          </div>
        </form>
      </section>
    </>
  )
}

const BASIC = ['retrieve', 'chat', 'ingest'] as const
const EXTRA = ['read_agents', 'message_history'] as const

/** AI 头像：点阵小球或开源的机器人头像，点一下就换（只记在这台设备上）。 */
function Avatars() {
  const current = useAvatar()
  return (
    <section className="settings-card" aria-label="AI 头像">
      <div className="settings-h">
        <h2>AI 头像</h2>
      </div>
      <p className="field-note">对话里 AI 用哪个头像。只是样子：回答前「在想」的那一行，照样按在做的事（理解问题、查资料、推理）换动作。只记在这台设备上。</p>
      {AVATAR_GROUPS.map((g) => (
        <div key={g.label} className="avatar-group" role="radiogroup" aria-label={g.label}>
          <h3>
            {g.label}
            <small>{g.note}</small>
          </h3>
          <div className="avatar-grid">
            {g.items.map((a) => {
              const on = sameAvatar(a, current)
              const name = a.kind === 'orb' ? `点阵 ${a.state}` : `${g.label} ${a.seed}`
              return (
                <button key={name} type="button" role="radio" aria-checked={on} aria-label={name} className="avatar-pick" onClick={() => setAvatar(a)}>
                  <AvatarMark choice={a} size={64} live={on} />
                </button>
              )
            })}
          </div>
        </div>
      ))}
      <p className="field-note">机器人头像来自开源的 DiceBear：Bottts 和 Bottts Neutral 是 Pablo Stanley 的设计（个人、商用都免费），Glass 是 MIT；点阵小球是 thinking-orbs（MIT）。都在浏览器里画，不连网。</p>
    </section>
  )
}

function Keys() {
  const qc = useQueryClient()
  const { message, modal } = AntApp.useApp()
  const me = useQuery({ queryKey: ['me'], queryFn: getMe })
  const tenant = me.data?.tenant?.id
  const kbs = (useKnowledgeBases().data ?? []).filter((k) => k.type !== 'faq')
  const keys = useQuery({ queryKey: ['api-keys', tenant], enabled: tenant !== undefined, queryFn: () => listApiKeys(tenant!) })
  const [creating, setCreating] = useState(false)
  const [made, setMade] = useState<(ApiKey & { token: string }) | null>(null)
  const revoke = useMutation({
    mutationFn: (id: number) => revokeApiKey(tenant!, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] })
      message.success('吊销了')
    },
    onError: (e) => void message.error(e.message),
  })
  const scope = (k: ApiKey) => (k.full_access ? '全部权限' : k.knowledge_base_ids?.length ? k.knowledge_base_ids.map((id) => kbs.find((x) => x.id === id)?.name ?? '（已删除的库）').join('、') : '全部知识库')
  return (
    <section className="settings-card" aria-label="API Key">
      <div className="settings-h">
        <h2>API Key</h2>
        <span className="grow" />
        <Pill size="sm" icon={<Plus />} onClick={() => setCreating(true)} disabled={tenant === undefined}>
          新建 API Key
        </Pill>
      </div>
      <p className="field-note">浏览器插件、命令行和别的 AI 用它来读写你的知识库。完整的 Key 只在新建时显示一次。</p>
      {keys.isError && <p className="field-note">没能载入：{keys.error.message}</p>}
      {keys.isSuccess && !keys.data.length && <p className="field-note">还没有 API Key。</p>}
      <ul className="key-list">
        {keys.data?.map((k) => (
          <li key={k.id} aria-label={k.name}>
            <KeyRound width={16} height={16} />
            <div className="key-main">
              <b>{k.name}</b>
              <code>{maskKey(k.api_key)}</code>
              <span className="key-caps">
                {(k.full_access ? ['全部权限'] : k.capabilities.map((c) => CAPABILITY_LABEL[c] ?? c)).map((c) => (
                  <span key={c} className="sk-chip">
                    {c}
                  </span>
                ))}
                <span className="field-note">{scope(k)}</span>
              </span>
              <span className="field-note">
                最近用过：{when(k.last_used_at)} · 建于 {when(k.created_at)}
                {k.expires_at ? ` · ${when(k.expires_at)} 过期` : ''}
              </span>
            </div>
            <Pill
              size="sm"
              className="danger"
              onClick={() =>
                modal.confirm({
                  title: `吊销「${k.name}」？`,
                  content: '用这个 Key 的地方（比如浏览器插件）会马上连不上，要换成新的 Key。',
                  okText: '吊销',
                  cancelText: '不了',
                  okButtonProps: { danger: true },
                  onOk: () => revoke.mutateAsync(k.id),
                })
              }
            >
              吊销
            </Pill>
          </li>
        ))}
      </ul>
      {creating && tenant !== undefined && (
        <NewKeyDialog
          tenant={tenant}
          kbs={kbs}
          onClose={() => setCreating(false)}
          onMade={(k) => {
            setCreating(false)
            setMade(k)
            qc.invalidateQueries({ queryKey: ['api-keys'] })
          }}
        />
      )}
      {made && <MadeKeyDialog k={made} onClose={() => setMade(null)} />}
    </section>
  )
}

function NewKeyDialog({ tenant, kbs, onClose, onMade }: { tenant: string | number; kbs: { id: string; name: string }[]; onClose: () => void; onMade: (k: ApiKey & { token: string }) => void }) {
  const { message } = AntApp.useApp()
  const [name, setName] = useState('')
  const [caps, setCaps] = useState<string[]>(['retrieve', 'chat', 'ingest'])
  const [scope, setScope] = useState<'all' | 'selected'>('all')
  const [picked, setPicked] = useState<string[]>([])
  const create = useMutation({
    mutationFn: () => createApiKey(tenant, { name: name.trim(), capabilities: caps, knowledge_base_ids: scope === 'selected' ? picked : [] }),
    onSuccess: onMade,
    onError: (e) => void message.error(e.message),
  })
  const toggle = (c: string) => setCaps((l) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]))
  const ok = name.trim() && caps.length && (scope === 'all' || picked.length)
  return (
    <Modal open title="新建 API Key" onCancel={onClose} footer={null} destroyOnHidden>
      <form
        className="dlg"
        onSubmit={(e) => {
          e.preventDefault()
          if (ok) create.mutate()
        }}
      >
        <label htmlFor="key-name">用在哪里</label>
        <input id="key-name" value={name} maxLength={60} placeholder="比如：公司电脑上的浏览器插件" onChange={(e) => setName(e.target.value)} autoFocus />
        <label>能做什么</label>
        <div className="kb-checks" role="group" aria-label="能做什么">
          {[...BASIC, ...EXTRA].map((c) => (
            <label key={c} className="kb-check">
              <input type="checkbox" checked={caps.includes(c)} onChange={() => toggle(c)} />
              {CAPABILITY_LABEL[c]}
            </label>
          ))}
        </div>
        <label>能碰哪些知识库</label>
        <div className="kb-checks" role="radiogroup" aria-label="能碰哪些知识库">
          <label className="kb-check">
            <input type="radio" name="key-scope" checked={scope === 'all'} onChange={() => setScope('all')} />
            全部
          </label>
          <label className="kb-check">
            <input type="radio" name="key-scope" checked={scope === 'selected'} onChange={() => setScope('selected')} />
            指定的
          </label>
        </div>
        {scope === 'selected' && (
          <div className="kb-checks" role="group" aria-label="指定的知识库">
            {kbs.map((k) => (
              <label key={k.id} className="kb-check">
                <input type="checkbox" checked={picked.includes(k.id)} onChange={(e) => setPicked((l) => (e.target.checked ? [...l, k.id] : l.filter((x) => x !== k.id)))} />
                {k.name}
              </label>
            ))}
          </div>
        )}
        <div className="dlg-acts">
          <Pill type="submit" variant="primary" disabled={!ok || create.isPending}>
            新建
          </Pill>
          <Pill onClick={onClose}>取消</Pill>
        </div>
      </form>
    </Modal>
  )
}

function MadeKeyDialog({ k, onClose }: { k: ApiKey & { token: string }; onClose: () => void }) {
  const { message } = AntApp.useApp()
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(k.token)
      message.success('复制好了')
    } catch {
      message.warning('没能自动复制，请手动选中复制')
    }
  }
  return (
    <Modal open title={`「${k.name}」建好了`} onCancel={onClose} footer={null} destroyOnHidden maskClosable={false}>
      <div className="dlg">
        <p className="field-note" data-bad="true">
          完整的 Key 只显示这一次，关掉以后就看不到了。先复制下来，贴到要用的地方。
        </p>
        <code className="key-token" aria-label="新的 API Key">
          {k.token}
        </code>
        <div className="dlg-acts">
          <Pill variant="primary" icon={<Copy />} onClick={() => void copy()}>
            复制
          </Pill>
          <Pill onClick={onClose}>我已经存好了</Pill>
        </div>
      </div>
    </Modal>
  )
}

const MODEL_TYPE: Record<string, string> = { KnowledgeQA: '对话', Embedding: '向量', Rerank: '重排', VLLM: '看图', ASR: '语音' }

function Models() {
  const models = useQuery({ queryKey: ['models'], queryFn: listModels })
  return (
    <section className="settings-card" aria-label="模型">
      <h2>模型</h2>
      <p className="field-note">内置模型写在部署配置里（lingzhi-server 的 deploy/lingzhi/builtin-models.yaml），这里只能看。思考室、写作教练、观点与证据用的是和对话同一个模型。</p>
      {models.isError && <p className="field-note">没能载入：{models.error.message}</p>}
      <table className="settings-table">
        <thead>
          <tr>
            <th>模型</th>
            <th>用途</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {models.data?.map((m) => (
            <tr key={m.id}>
              <td>
                <span>{m.display_name || m.name}</span>
                <small>{m.id}</small>
              </td>
              <td>{MODEL_TYPE[m.type] ?? m.type}</td>
              <td>
                {m.is_default && <span className="sk-chip">默认</span>}
                {(m as { is_builtin?: boolean }).is_builtin && <span className="sk-chip">内置</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

const uptime = (s?: number) => {
  if (!s) return '—'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  return d ? `${d} 天 ${h} 小时` : `${h} 小时 ${Math.floor((s % 3600) / 60)} 分`
}

function About() {
  const info = useQuery({ queryKey: ['system-info'], queryFn: systemInfo })
  const i = info.data
  return (
    <section className="settings-card" aria-label="关于">
      <h2>关于</h2>
      {info.isError && <p className="field-note">没能载入：{info.error.message}</p>}
      <dl className="settings-dl">
        <dt>服务端</dt>
        <dd>
          WeKnora {i?.version ?? '…'}
          {i?.edition ? `（${i.edition}）` : ''}
          {i?.commit_id ? ` · ${i.commit_id.slice(0, 8)}` : ''}
        </dd>
        <dt>已运行</dt>
        <dd>{uptime(i?.uptime_seconds)}</dd>
        <dt>检索</dt>
        <dd>
          {[i?.keyword_index_engine && `关键词：${i.keyword_index_engine}`, i?.vector_store_engine && `向量：${i.vector_store_engine}`, i?.graph_database_engine && `关联图：${i.graph_database_engine}`].filter(Boolean).join(' · ') || '…'}
        </dd>
        <dt>数据结构</dt>
        <dd>{i?.db_version ? `第 ${i.db_version} 版` : '…'}</dd>
        <dt>网页</dt>
        <dd>lingzhi-web（React）</dd>
      </dl>
    </section>
  )
}
